import { Loader2, Sparkles } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchJson, sendJson } from "../../api/rest";
import type { ShopText } from "../../types/shop-texts";
import { btn, ErrorBanner, fmtDateTime, input } from "../flows/ui";

const POLL_MS = 3000;
const POLL_LIMIT_MS = 120_000;

export const ShopTextPanel = ({ merchantId }: { merchantId: string }) => {
	const [data, setData] = useState<ShopText | null>(null);
	const [draft, setDraft] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [rewriting, setRewriting] = useState(false);
	const [saving, setSaving] = useState(false);
	const timer = useRef<ReturnType<typeof setInterval> | null>(null);

	const load = useCallback(async () => {
		const next = await fetchJson<ShopText>(
			`/shop-texts/merchants/${merchantId}`,
		);
		setData(next);
		setDraft(next.text ?? "");
		return next;
	}, [merchantId]);

	useEffect(() => {
		load().catch((e) => setError(e instanceof Error ? e.message : String(e)));
		return () => {
			if (timer.current) clearInterval(timer.current);
		};
	}, [load]);

	const rewrite = async () => {
		setError(null);
		setNotice(null);
		setRewriting(true);
		const before = { at: data?.at ?? null, error: data?.error ?? null };
		try {
			await sendJson("/shop-texts/rewrite", "POST", { slug: merchantId });
		} catch (e) {
			setRewriting(false);
			setError(e instanceof Error ? e.message : String(e));
			return;
		}
		const started = Date.now();
		timer.current = setInterval(async () => {
			const next = await load().catch(() => null);
			const changed =
				next && (next.at !== before.at || next.error !== before.error);
			if (changed || Date.now() - started > POLL_LIMIT_MS) {
				if (timer.current) clearInterval(timer.current);
				setRewriting(false);
				if (!changed) setNotice("Still working. Reload the page in a minute.");
			}
		}, POLL_MS);
	};

	const save = async () => {
		setError(null);
		setNotice(null);
		setSaving(true);
		try {
			const result = await sendJson<{
				success: boolean;
				problems: string[];
				error?: string;
			}>(`/shop-texts/merchants/${merchantId}`, "PUT", { text: draft });
			if (!result.success) setError(result.error ?? "Could not save");
			else if (result.problems.length > 0)
				setNotice(`Saved, but note: ${result.problems.join(" ")}`);
			else setNotice("Saved.");
			await load();
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="card bg-white p-5">
			<div className="mb-3 flex flex-wrap items-baseline justify-between gap-3">
				<div>
					<h3 className="text-sm font-semibold text-slate-700">Shop text</h3>
					<p className="text-[12px] text-slate-500">
						The description shoppers see on the shop page. Written by the model
						from the network&apos;s own text, which nightly syncs keep
						overwriting.
					</p>
				</div>
				<button
					type="button"
					className={btn.primary}
					disabled={rewriting}
					onClick={rewrite}
				>
					{rewriting ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : (
						<Sparkles className="h-4 w-4" />
					)}
					{data?.text ? "Rewrite with AI" : "Write with AI"}
				</button>
			</div>

			<ErrorBanner error={error} />
			{notice && <p className="mb-2 text-[12px] text-slate-600">{notice}</p>}

			{data && (
				<div className="space-y-3">
					<textarea
						className={`${input} min-h-[110px] resize-y`}
						value={draft}
						placeholder="No shop text yet. The page shows the network's text with affiliate sentences removed."
						onChange={(e) => setDraft(e.target.value)}
					/>
					<div className="flex flex-wrap items-center justify-between gap-3 text-[11px] text-slate-500">
						<span>
							{data.text
								? `${data.model === "manual" ? "Edited by hand" : `Written by ${data.model}`} · ${fmtDateTime(data.at)}`
								: "Not written yet"}
							{data.stale && (
								<span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-amber-700">
									network text changed since
								</span>
							)}
							{data.error && (
								<span className="ml-2 rounded bg-red-50 px-1.5 py-0.5 text-red-700">
									last attempt failed: {data.error}
								</span>
							)}
						</span>
						<button
							type="button"
							className={btn.ghost}
							disabled={saving || draft === (data.text ?? "")}
							onClick={save}
						>
							Save text
						</button>
					</div>
					<details className="text-[12px]">
						<summary className="cursor-pointer text-slate-500">
							Network text (source)
						</summary>
						<p className="mt-2 whitespace-pre-line text-slate-600">
							{data.original || "(empty)"}
						</p>
					</details>
				</div>
			)}
		</div>
	);
};
